// Model Provider Backend Service
// Handles all provider operations through the governance backend API

import { unifiedApi } from '@/lib/api-client';
import { isApiFailure } from '@agion/ui/api-failure';
import type { ModelModality, ReasoningEffort } from './model-capabilities';

export type { ModelModality, ReasoningEffort } from './model-capabilities';
export {
  extractBackendCapabilities,
  modelSupportsModality,
  normalizeModelModality,
  resolveModelCapabilities,
} from './model-capabilities';

export type FoundryManagementMode = 'agion' | 'external';
export type AzureAuthMode = 'api_key' | 'managed_identity';

// Provider types
export type ProviderType =
  | 'openai'
  | 'anthropic'
  | 'azure_openai'
  | 'azure_ai_foundry'
  | 'azure_cognitive_services'
  | 'azure_apim'
  | 'google_vertex'
  | 'huggingface'
  | 'replicate'
  | 'openrouter'
  | 'elevenlabs'
  | 'cerebras'
  | 'mistral'
  | 'modulate'
  | 'cloudflare'
  | 'local'
  | 'custom';

// Backend API types - Note: API client transforms snake_case to camelCase
// So we define camelCase here which matches what the frontend sees after transformation
export interface ModelProvider {
  id: string;
  organizationId: string;
  name: string;
  providerType: ProviderType;
  baseUrl?: string;
  apiVersion?: string;
  deploymentName?: string;
  rateLimitRpm?: number;
  monthlyBudgetUsd?: number;
  currentMonthSpend: number;
  lastBudgetReset: string;
  status: 'active' | 'inactive' | 'suspended' | 'failed';
  healthStatus: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  healthCheckAt?: string;
  healthErrorMessage?: string;
  enabledModels: string[]; // Model IDs that are enabled for use
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  // Azure API Management specific fields
  apimSubscriptionKeyHeader?: string; // Header name for subscription key (default: Ocp-Apim-Subscription-Key)
  apimOidcEnabled?: boolean; // Enable OIDC validation in APIM
  apimOidcConfigUrl?: string; // OpenID Connect configuration URL
  apimOidcIssuer?: string; // OIDC token issuer
  apimOidcAudience?: string; // OIDC token audience (auto-derived from APIM URL)
  // Credential status (computed by backend — true if API key exists in vault or AES)
  hasApiKey?: boolean;
  // Azure Service Principal fields (read-only, client_id and tenant_id are not secrets)
  spClientId?: string; // Azure AD Service Principal client ID
  spTenantId?: string; // Azure AD tenant ID
  // Azure resource targeting fields (for SP-based deployments)
  azureSubscriptionId?: string; // Azure subscription ID
  azureResourceGroup?: string; // Azure resource group name
  azureAccountName?: string; // Azure AI account name
  managementMode?: FoundryManagementMode;
  azureAuthMode?: AzureAuthMode;
  azureIdentityBinding?: string;
  // Azure region (populated from ARM API)
  azureRegion?: string; // Azure region (e.g., swedencentral)
  // Azure Cognitive Services account kind — OpenAI | AIServices | CognitiveServices
  azureKind?: string;
  // Raw (pre-dedup) count of entries from the last Azure catalog refresh
  totalRawVersionCount?: number;
  // Per-model deployment configurations
  modelConfigurations?: Record<string, ModelConfigEntry>;
  // Google Vertex AI fields
  gcpProjectId?: string; // GCP project ID
  gcpLocation?: string; // GCP region (e.g., us-central1)
}

export interface ProviderReasoningCapabilities {
  supportedEfforts?: ReasoningEffort[];
  defaultEffort?: ReasoningEffort;
  defaultEnabled?: boolean;
  supportsMaxTokens?: boolean;
  mandatory?: boolean;
  supportsTools?: boolean;
}

// Native facts: absence, null, and a finite list have different meanings.
// Unknown effort strings are retained as evidence, not authorized controls.
export interface NativeReasoningCapabilities {
  supportedEfforts?: string[] | null;
  defaultEffort?: string;
  defaultEnabled?: boolean;
  supportsMaxTokens?: boolean;
  mandatory?: boolean;
}

export interface CapabilityEvidence {
  schemaVersion: number;
  source: string;
  sourceRevision?: string;
  observedAt?: string;
}

export interface InferenceDefaultsConfig {
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  stop?: string[];
  inferenceGeo?: string; // Anthropic only: "us" | "global"
  reasoningEffort?: ReasoningEffort;
}

export interface AzureDeploymentConfig {
  raiPolicyName?: string;
  versionUpgradeOption?: string; // NoAutoUpgrade | OnceCurrentVersionExpired | OnceNewDefaultVersionAvailable
  modelFormat?: string;
  modelVersion?: string;
  deploymentName?: string;
}

export interface OpenRouterRoutingConfig {
  reasoning?: NativeReasoningCapabilities;
  capabilityEvidence?: CapabilityEvidence;
  providerName: string;
  tag: string;
  allowFallbacks: boolean;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
  cacheReadTokenPrice?: number;
  cacheWriteTokenPrice?: number;
  contextWindow?: number;
  maxOutputTokens?: number;
  maxPromptTokens?: number;
  quantization?: string;
  supportedParameters?: string[];
  uptimeLast30m?: number;
  status?: number;
  pricingSyncedAt?: string;
}

export interface ModelConfigEntry {
  skuName: string;
  capacity: number;
  enabled: boolean;
  azure?: AzureDeploymentConfig;
  inference?: InferenceDefaultsConfig;
  openrouter?: OpenRouterRoutingConfig;
}

export interface CreateModelProviderRequest {
  name: string;
  providerType: ProviderType;
  baseUrl?: string;
  apiVersion?: string;
  deploymentName?: string;
  apiKey?: string; // Will be encrypted by backend (required for API key auth)
  rateLimitRpm?: number;
  monthlyBudgetUsd?: number;
  // Azure API Management specific fields
  apimSubscriptionKeyHeader?: string; // Header name for subscription key (default: Ocp-Apim-Subscription-Key)
  apimOidcEnabled?: boolean; // Enable OIDC validation in APIM
  apimOidcConfigUrl?: string; // OpenID Connect configuration URL
  apimOidcIssuer?: string; // OIDC token issuer
  apimOidcAudience?: string; // OIDC token audience (auto-derived from APIM URL)
  // Azure Service Principal fields (for direct Azure AI Foundry deployment)
  spClientId?: string; // Azure AD Service Principal client ID
  spTenantId?: string; // Azure AD tenant ID
  spClientSecret?: string; // Client secret (stored in OpenBao, not DB)
  // Azure resource targeting fields (for SP-based deployments)
  azureSubscriptionId?: string; // Azure subscription ID
  azureResourceGroup?: string; // Azure resource group name
  azureAccountName?: string; // Azure AI account name
  managementMode?: FoundryManagementMode;
  azureAuthMode?: AzureAuthMode;
  azureIdentityBinding?: string;
  // Google Vertex AI fields
  gcpProjectId?: string; // GCP project ID
  gcpLocation?: string; // GCP region (e.g., us-central1)
}

export interface UpdateModelProviderRequest {
  name?: string;
  baseUrl?: string;
  apiVersion?: string;
  deploymentName?: string;
  apiKey?: string; // If provided, will be encrypted
  rateLimitRpm?: number;
  monthlyBudgetUsd?: number;
  status?: 'active' | 'inactive' | 'suspended' | 'failed';
  enabledModels?: string[]; // Model IDs to enable
  modelConfigurations?: Record<string, ModelConfigEntry>; // Per-model deployment settings
  // Azure API Management specific fields
  apimSubscriptionKeyHeader?: string; // Header name for subscription key
  apimOidcEnabled?: boolean; // Enable OIDC validation in APIM
  apimOidcConfigUrl?: string; // OpenID Connect configuration URL
  apimOidcIssuer?: string; // OIDC token issuer
  apimOidcAudience?: string; // OIDC token audience
  // Azure Service Principal fields (for direct Azure AI Foundry deployment)
  spClientId?: string; // Azure AD Service Principal client ID
  spTenantId?: string; // Azure AD tenant ID
  spClientSecret?: string; // Client secret (stored in OpenBao, not DB)
  // Azure resource targeting fields (for SP-based deployments)
  azureSubscriptionId?: string; // Azure subscription ID
  azureResourceGroup?: string; // Azure resource group name
  azureAccountName?: string; // Azure AI account name
  managementMode?: FoundryManagementMode;
  azureAuthMode?: AzureAuthMode;
  azureIdentityBinding?: string;
  // Google Vertex AI fields
  gcpProjectId?: string; // GCP project ID
  gcpLocation?: string; // GCP region (e.g., us-central1)
}

// Request to test provider credentials without creating a provider record
export interface TestProviderCredentialsRequest {
  providerType: ProviderType;
  apiKey?: string;
  baseUrl?: string;
  apiVersion?: string;
  deploymentName?: string;
  apimSubscriptionKeyHeader?: string;
  gcpProjectId?: string;
  gcpLocation?: string;
  spClientId?: string;
  spTenantId?: string;
  spClientSecret?: string;
  azureSubscriptionId?: string;
  azureResourceGroup?: string;
  azureAccountName?: string;
  managementMode?: FoundryManagementMode;
  azureAuthMode?: AzureAuthMode;
  azureIdentityBinding?: string;
}

// Response from testing provider credentials
export interface TestProviderCredentialsResponse {
  success: boolean;
  message: string;
  responseTimeMs: number;
  testedAt: string;
}

export interface ListProvidersResponse {
  providers: ModelProvider[];
  total: number;
  limit: number;
  offset: number;
}

export interface OpenRouterModelEndpoint {
  reasoning?: NativeReasoningCapabilities;
  capabilityEvidence?: CapabilityEvidence;
  providerName: string;
  tag: string;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
  cacheReadTokenPrice?: number;
  cacheWriteTokenPrice?: number;
  contextWindow?: number;
  maxOutputTokens?: number;
  maxPromptTokens?: number;
  quantization?: string;
  supportedParameters?: string[];
  uptimeLast30m?: number;
  status?: number;
}

export interface OpenRouterModelEndpointsResponse {
  modelId: string;
  modelName?: string;
  endpoints: OpenRouterModelEndpoint[];
}

// Language support info (from ElevenLabs /v1/models API)
export interface ModelLanguage {
  language_id: string; // e.g., "en", "fi", "sv"
  name: string; // e.g., "English", "Finnish", "Swedish"
}

export interface AvailableModel {
  id: string;
  organizationId: string;
  providerId: string;
  modelIdentifier: string;
  name?: string; // Backend returns "name" (e.g., "Voice: Tomi (premade)")
  displayName: string;
  description?: string;
  contextWindow?: number;
  maxOutputTokens?: number;
  supportsFunctions: boolean;
  supportsStreaming: boolean;
  supportsVision: boolean;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
  isAvailable: boolean;
  status: 'active' | 'inactive' | 'deprecated';
  createdAt: string;
  updatedAt: string;
  primaryModality?: string;
  displayModality?: string;
  modality?: ModelModality;
  providerName?: string;
  providerType?: string;
  capabilities?: string[];
  inputModalities?: string[];
  outputModalities?: string[];
  supportedParameters?: string[];
  protocol?: string;
  reasoning?: ProviderReasoningCapabilities;
  nativeReasoning?: NativeReasoningCapabilities;
  capabilityEvidence?: CapabilityEvidence;
  format?: string;
  catalogVersion?: string;
  languages?: ModelLanguage[];
  // HuggingFace multi-provider support (Feb 2026)
  inferenceProviders?: InferenceProviderInfo[];
}

export interface InferenceProviderInfo {
  provider_name: string; // "together", "sambanova", "fireworks", "groq", etc.
  status: string; // "live" or "staging"
  task: string; // "conversational", "text-to-image", etc.
}

// ---- Provider deletion preview (GET providers/{id}/dependencies) ----
// Wire format is snake_case; unifiedApi.get transforms it to the camelCase
// shapes below (removed_with_provider -> removedWithProvider, model_id ->
// modelId, replacement_available -> replacementAvailable).

export type ProviderDeletionImpactItemType =
  | 'provider_model'
  | 'organization_default'
  | 'agent_permission'
  | 'llm_deployment';

// One record the database removes together with the provider.
export interface ProviderDeletionImpactItem {
  type: ProviderDeletionImpactItemType;
  id: string;
  name: string;
  modelId?: string;
  detail?: string;
}

// A record that prevents the delete outright. The V276 guard refuses to drop a
// provider that still owns a deployment outside (failed, deleted): the remote
// Azure deployment may still be running, and only the deployment state machine
// can remove it.
export interface ProviderDeletionImpactBlocker {
  type: 'llm_deployment';
  id: string;
  name: string;
  status: string;
  reason: string;
}

export type ProviderDeletionConsumerType = 'agent' | 'mission_orchestrator' | 'mission_workflow';

// A consumer that survives the delete but whose configured model the provider
// currently serves. `replacementAvailable` false means no other provider
// serves that model, so the consumer breaks on deletion.
export interface ProviderDeletionAffectedConsumer {
  type: ProviderDeletionConsumerType;
  id: string;
  name: string;
  modelId: string;
  replacementAvailable: boolean;
}

export interface ProviderDeletionImpact {
  providerId: string;
  providerName: string;
  // Non-empty means the server will refuse the delete with a 409.
  blockers: ProviderDeletionImpactBlocker[];
  removedWithProvider: ProviderDeletionImpactItem[];
  affectedConsumers: ProviderDeletionAffectedConsumer[];
  ledgerRetained: boolean;
}

export interface CreateAvailableModelRequest {
  providerId: string;
  modelIdentifier: string;
  displayName: string;
  description?: string;
  contextWindow?: number;
  maxOutputTokens?: number;
  supportsFunctions?: boolean;
  supportsStreaming?: boolean;
  supportsVision?: boolean;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
}

export interface ProviderDeployment {
  id: string;
  organizationId?: string;
  providerId?: string;
  azureDeploymentName: string;
  modelName: string;
  modelVersion?: string;
  modelFormat?: string;
  skuName?: string;
  skuCapacity?: number;
  status: string;
  statusMessage?: string;
  endpointUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListProviderDeploymentsResponse {
  deployments: ProviderDeployment[];
  total: number;
  limit: number;
  offset: number;
}

export interface FoundryIdentityBinding {
  id: string;
  accountName: string;
  endpointOrigin: string;
  eligible: boolean;
  unavailableReasonCode?: string;
}

export interface ProviderModeOption<T extends string> {
  id: T;
  eligible: boolean;
  unavailableReasonCode?: string;
}

export interface ExternalFoundryDeployment {
  deploymentName: string;
  modelName: string;
  modelFormat: string;
  modelVersion: string;
  skuName: string;
  skuCapacity: number;
  provisioningState: string;
  selectable: boolean;
  unavailableReasonCode?: string;
}

class ProviderService {
  async listFoundryIdentityBindings(): Promise<{
    managementModes: ProviderModeOption<FoundryManagementMode>[];
    azureAuthModes: ProviderModeOption<AzureAuthMode>[];
    bindings: FoundryIdentityBinding[];
  }> {
    return await unifiedApi.get<{
      managementModes: ProviderModeOption<FoundryManagementMode>[];
      azureAuthModes: ProviderModeOption<AzureAuthMode>[];
      bindings: FoundryIdentityBinding[];
    }>('providers/foundry-identity-bindings');
  }

  // Create a new model provider
  async createProvider(request: CreateModelProviderRequest): Promise<ModelProvider> {
    return await unifiedApi.post<ModelProvider, CreateModelProviderRequest>('providers', request);
  }

  // Get a specific provider
  async getProvider(providerId: string): Promise<ModelProvider> {
    return await unifiedApi.get<ModelProvider>(`providers/${providerId}`);
  }

  // List all providers for the current organization
  async listProviders(params?: {
    status?: string;
    provider_type?: string;
  }): Promise<ListProvidersResponse> {
    return await unifiedApi.get<ListProvidersResponse>('providers', params);
  }

  // Update a provider
  async updateProvider(
    providerId: string,
    request: UpdateModelProviderRequest
  ): Promise<ModelProvider> {
    return await unifiedApi.put<ModelProvider, UpdateModelProviderRequest>(
      `providers/${providerId}`,
      request
    );
  }

  // Delete a provider
  async deleteProvider(providerId: string): Promise<void> {
    return await unifiedApi.delete<void>(`providers/${providerId}`);
  }

  // Deletion preview: what the delete removes with the provider, and which
  // consumers keep referencing its models afterwards.
  async getProviderDependencies(providerId: string): Promise<ProviderDeletionImpact> {
    return await unifiedApi.get<ProviderDeletionImpact>(`providers/${providerId}/dependencies`);
  }

  // List Azure deployment rows for a provider (capacity resize / rollout
  // status). The adapter camelizes the snake_case wire fields.
  async listProviderDeployments(providerId: string): Promise<ListProviderDeploymentsResponse> {
    return await unifiedApi.get<ListProviderDeploymentsResponse>(
      `providers/${providerId}/deployments`
    );
  }

  async listExternalFoundryDeployments(
    providerId: string
  ): Promise<{ deployments: ExternalFoundryDeployment[] }> {
    return await unifiedApi.get<{ deployments: ExternalFoundryDeployment[] }>(
      `providers/${providerId}/azure-deployments`
    );
  }

  // Test provider credentials without creating a provider record (standalone)
  async testProviderCredentials(
    request: TestProviderCredentialsRequest
  ): Promise<TestProviderCredentialsResponse> {
    return await unifiedApi.post<TestProviderCredentialsResponse>('providers/test', {
      provider_type: request.providerType,
      api_key: request.apiKey,
      base_url: request.baseUrl,
      api_version: request.apiVersion,
      deployment_name: request.deploymentName,
      apim_subscription_key_header: request.apimSubscriptionKeyHeader,
      gcp_project_id: request.gcpProjectId,
      gcp_location: request.gcpLocation,
      sp_client_id: request.spClientId,
      sp_tenant_id: request.spTenantId,
      sp_client_secret: request.spClientSecret,
      azure_subscription_id: request.azureSubscriptionId,
      azure_resource_group: request.azureResourceGroup,
      azure_account_name: request.azureAccountName,
      management_mode: request.managementMode,
      azure_auth_mode: request.azureAuthMode,
      azure_identity_binding: request.azureIdentityBinding,
    });
  }

  // Whether this environment offers a provider type: governance-service
  // answers its capabilities read for a type it offers and 404 for one it
  // does not (Cloudflare Workers AI, challenge condition C9). Any other
  // failure is not an answer and is thrown.
  async isProviderTypeOffered(providerType: string): Promise<boolean> {
    try {
      await unifiedApi.get<unknown>('providers/capabilities', { provider_type: providerType });
      return true;
    } catch (error) {
      if (isApiFailure(error) && error.status === 404) return false;
      throw error;
    }
  }

  // Test existing provider connection (requires provider ID)
  async testConnection(providerId: string): Promise<{ success: boolean; message?: string }> {
    return await unifiedApi.post<{ success: boolean; message?: string }>(
      `providers/${providerId}/test`
    );
  }

  // List models for a provider (fetches from provider API directly)
  // Pass refresh=true to bypass cache and get fresh data from the provider API.
  // warnings: regional availability warnings (currently Vertex AI only)
  async listModels(
    providerId: string,
    options?: { refresh?: boolean }
  ): Promise<{
    models: AvailableModel[];
    warnings?: Array<{
      code: string;
      message: string;
      severity: string;
      dismissible: boolean;
      metadata: Record<string, any>;
    }>;
    azureRegion?: string;
  }> {
    const params = options?.refresh ? { refresh: 'true' } : undefined;
    return await unifiedApi.get<{
      models: AvailableModel[];
      warnings?: Array<{
        code: string;
        message: string;
        severity: string;
        dismissible: boolean;
        metadata: Record<string, any>;
      }>;
      azureRegion?: string;
    }>(`providers/${providerId}/models`, params);
  }

  async getOpenRouterEndpoints(
    providerId: string,
    modelId: string
  ): Promise<OpenRouterModelEndpointsResponse> {
    return await unifiedApi.get<OpenRouterModelEndpointsResponse>(
      `providers/${providerId}/openrouter-endpoints`,
      {
        model_id: modelId,
      }
    );
  }

  // List models from all active providers in a single call
  // Note: unifiedApi transforms snake_case → camelCase, so types are camelCase
  async listAllModels(): Promise<{
    providers: Array<{
      providerId: string;
      providerName: string;
      providerType: string;
      baseUrl?: string;
      models: AvailableModel[];
      error?: string;
    }>;
  }> {
    return await unifiedApi.get<{
      providers: Array<{
        providerId: string;
        providerName: string;
        providerType: string;
        baseUrl?: string;
        models: AvailableModel[];
        error?: string;
      }>;
    }>('providers/models');
  }

  // Get available Vertex AI regions from backend
  async getVertexRegions(): Promise<Array<{ id: string; name: string; displayName: string }>> {
    interface BackendRegionsResponse {
      regions: Array<{
        id: string;
        name: string;
        display_name: string;
      }>;
      total: number;
    }

    const response = await unifiedApi.get<BackendRegionsResponse>(
      'providers/google-vertex/regions'
    );

    // unifiedApi automatically converts snake_case to camelCase, but we handle both formats for safety
    return response.regions.map((r) => ({
      id: r.id,
      name: r.name,
      displayName: (r as any).displayName || r.display_name, // Support both camelCase and snake_case
    }));
  }

  // NOTE: The following methods are not implemented in the backend
  // Models are fetched directly from provider APIs, not managed in our database
  // Keeping stubs for future implementation if needed

  // Add a model to a provider
  // TODO: Backend endpoint POST /v1/providers/{id}/models does not exist
  async addModel(
    _providerId: string,
    _request: CreateAvailableModelRequest
  ): Promise<AvailableModel> {
    throw new Error('Adding models is not supported - models are fetched from provider API');
  }

  // Remove a model from a provider
  // TODO: Backend endpoint DELETE /v1/providers/{id}/models/{modelId} does not exist
  async removeModel(_providerId: string, _modelId: string): Promise<void> {
    throw new Error('Removing models is not supported - models are fetched from provider API');
  }

  // Get provider health status
  async getProviderHealth(providerId: string): Promise<{
    status: string;
    lastCheck: string;
    errorMessage?: string;
  }> {
    return await unifiedApi.get<{
      status: string;
      lastCheck: string;
      errorMessage?: string;
    }>(`providers/${providerId}/health`);
  }
}

export const providerService = new ProviderService();
